import React from 'react';
import { X } from 'lucide-react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { useCapabilities } from '../../../hooks/useCapabilities';
import { invoiceTabForDocument } from '../utils/incomingInvoice';
import type { InvoicePartyDisplayContext } from '../utils/invoicePartyDisplay';
import type { ContactOption, InvoiceDocument, LeadOption, OrganizationOption } from '../types';
import { InvoiceDocumentPreviewModal } from './InvoiceDocumentPreviewModal';

function mapOrganizations(payload: unknown): OrganizationOption[] {
  if (!Array.isArray(payload)) return [];
  return payload
    .map((o: { id?: string; name?: string; organization_type?: string }) => ({
      id: String(o.id ?? ''),
      name: String(o.name ?? '').trim(),
      organization_type: String(o.organization_type ?? ''),
    }))
    .filter((o) => o.id && o.name);
}

export function InvoicePreviewModalHost({
  invoiceId,
  onClose,
  onPatched,
}: {
  invoiceId: string;
  onClose: () => void;
  onPatched?: () => void;
}) {
  const { can, loaded: capabilitiesLoaded } = useCapabilities();
  // Projekte und Kontakte sind eigene Module: Auswahllisten nur mit Leserecht laden.
  const canViewProjects = can('web.projekte.view');
  const canViewContacts = can('web.kontakte.view');
  const [viewId, setViewId] = React.useState(invoiceId);
  const [row, setRow] = React.useState<InvoiceDocument | null>(null);
  const [leads, setLeads] = React.useState<LeadOption[]>([]);
  const [contacts, setContacts] = React.useState<ContactOption[]>([]);
  const [organizations, setOrganizations] = React.useState<OrganizationOption[]>([]);
  const [incomingCreditSources, setIncomingCreditSources] = React.useState<InvoiceDocument[]>([]);
  const [constructionDeductionsEnabled, setConstructionDeductionsEnabled] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setViewId(invoiceId);
  }, [invoiceId]);

  React.useEffect(() => {
    if (!capabilitiesLoaded) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetch(`/api/invoices/${encodeURIComponent(viewId)}`, { credentials: 'include' }),
      canViewProjects ? fetch('/api/leads', { credentials: 'include' }) : null,
      canViewContacts ? fetch('/api/contacts', { credentials: 'include' }) : null,
      canViewContacts ? fetch('/api/organizations', { credentials: 'include' }) : null,
      fetch('/api/settings/company', { credentials: 'include' }),
    ])
      .then(async ([invoiceRes, leadRes, contactRes, orgRes, settingsRes]) => {
        const invoicePayload = await invoiceRes.json().catch(() => ({}));
        if (!invoiceRes.ok) {
          throw new Error(invoicePayload?.error || 'Rechnung konnte nicht geladen werden.');
        }
        const [leadPayload, contactPayload, orgPayload, settingsPayload] = await Promise.all([
          leadRes?.ok ? leadRes.json() : [],
          contactRes?.ok ? contactRes.json() : [],
          orgRes?.ok ? orgRes.json() : [],
          settingsRes.ok ? settingsRes.json() : {},
        ]);
        if (cancelled) return;
        setRow(invoicePayload as InvoiceDocument);
        setLeads(Array.isArray(leadPayload) ? leadPayload : []);
        setContacts(Array.isArray(contactPayload) ? contactPayload : []);
        setOrganizations(mapOrganizations(orgPayload));
        setConstructionDeductionsEnabled(
          (settingsPayload as { construction_deductions_enabled?: boolean }).construction_deductions_enabled === true,
        );
        const invoice = invoicePayload as InvoiceDocument;
        if (invoiceTabForDocument(invoice) === 'incoming') {
          const params = new URLSearchParams({ type: 'incoming', per_page: '100', status: 'all' });
          const orgId = String(invoice.organization_id ?? '').trim();
          if (orgId) params.set('organization_id', orgId);
          const listRes = await fetch(`/api/invoices?${params.toString()}`, { credentials: 'include' });
          const listPayload = listRes.ok ? await listRes.json().catch(() => ({})) : {};
          const items = Array.isArray(listPayload?.items) ? listPayload.items : [];
          if (!cancelled) setIncomingCreditSources(items);
        } else if (!cancelled) {
          setIncomingCreditSources([]);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setRow(null);
          setError(err instanceof Error ? err.message : 'Rechnung konnte nicht geladen werden.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [viewId, capabilitiesLoaded, canViewProjects, canViewContacts]);

  const partyDisplayContext = React.useMemo<InvoicePartyDisplayContext>(() => {
    const leadsById = new Map<string, Pick<LeadOption, 'contact' | 'contact_id' | 'name'>>();
    for (const lead of leads) {
      leadsById.set(String(lead.id), lead);
    }
    const organizationNameById = new Map<string, string>();
    for (const org of organizations) {
      organizationNameById.set(org.id, org.name);
    }
    return { leadsById, contacts, organizationNameById };
  }, [leads, contacts, organizations]);

  const onPatch = async (patch: Record<string, unknown>): Promise<boolean> => {
    if (!row) return false;
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(`/api/invoices/${encodeURIComponent(row.id)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Eintrag konnte nicht aktualisiert werden.');
      setRow((prev) => (prev ? { ...prev, ...(payload as Partial<InvoiceDocument>) } : prev));
      onPatched?.();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Eintrag konnte nicht aktualisiert werden.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="fixed inset-0 z-[260] flex items-stretch md:items-center justify-center bg-slate-900/55 p-0 md:p-4"
        role="dialog"
        aria-modal="true"
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <div
          className="flex h-full w-full flex-col overflow-hidden rounded-none border-0 bg-app-surface shadow-2xl md:h-[86vh] md:max-w-6xl md:rounded-xl md:border md:border-app-border"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-end gap-2 border-b border-app-border bg-app-surface-muted px-5 py-3">
            <button type="button" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 hover:bg-slate-200 hover:text-app-text-muted">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex flex-1 items-center justify-center p-6">
            <InlineLoadingState label="Rechnung wird geladen …" />
          </div>
        </div>
      </div>
    );
  }

  if (!row) {
    return (
      <div
        className="fixed inset-0 z-[260] flex items-stretch md:items-center justify-center bg-slate-900/55 p-0 md:p-4"
        role="dialog"
        aria-modal="true"
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <div
          className="flex h-full w-full flex-col overflow-hidden rounded-none border-0 bg-app-surface shadow-2xl md:h-auto md:max-w-md md:rounded-xl md:border md:border-app-border"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-between gap-3 border-b border-app-border bg-app-surface-muted px-5 py-3">
            <h2 className="text-sm font-semibold text-app-text">Rechnung</h2>
            <button type="button" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 hover:bg-slate-200 hover:text-app-text-muted">
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="px-5 py-4 text-sm text-rose-700">{error || 'Rechnung konnte nicht geladen werden.'}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {error ? (
        <div className="pointer-events-none fixed bottom-4 left-1/2 z-[270] -translate-x-1/2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 shadow">
          {error}
        </div>
      ) : null}
      <InvoiceDocumentPreviewModal
        row={row}
        tab={invoiceTabForDocument(row)}
        leads={leads}
        contacts={contacts}
        organizations={organizations}
        partyDisplayContext={partyDisplayContext}
        saving={saving}
        onPatch={onPatch}
        onClose={onClose}
        onOpenDuplicate={(id) => setViewId(id)}
        onOrganizationCreated={(org) => setOrganizations((prev) => [...mapOrganizations([org]), ...prev])}
        incomingCreditSources={incomingCreditSources}
        constructionDeductionsEnabled={constructionDeductionsEnabled}
        onReviewed={(next) => {
          setRow((prev) => (prev ? { ...prev, ...next } : next));
          onPatched?.();
        }}
      />
    </>
  );
}
